/**
 * ManualRollModal - Prompt for user-entered dice results (generic XdY)
 * date_of_change: 2025-10-13
 * rule_reference: AGENTS.md (separate rules engine from presentation; expose GM hooks)
 */
import React, { useState, useEffect, useMemo } from 'react';
import BattleRoomDialog from './BattleRoomDialog';

const ManualRollModal = ({
  visible = false,
  title = 'Enter Roll',
  prompt = 'Enter dice results',
  numDice = 1,
  dieSize = 20,
  onConfirm,
  onCancel
}) => {
  const [values, setValues] = useState([]);

  const count = useMemo(() => {
    const n = Math.max(1, Math.min(100, Number(numDice) || 1));
    return n;
  }, [numDice]);
  const normalizedDieSize = Math.max(2, Number(dieSize) || 20);
  const valuesAreValid = values.length === count && values.every((value) => {
    const parsed = Number(value);
    return String(value).trim() !== ''
      && Number.isInteger(parsed)
      && parsed >= 1
      && parsed <= normalizedDieSize;
  });

  useEffect(() => {
    if (visible) {
      setValues(Array.from({ length: count }, () => ''));
    }
  }, [visible, count]);

  if (!visible) return null;

  return (
    <BattleRoomDialog
      visible={visible}
      eyebrow="Manual roll"
      title={title}
      description={`${prompt} (d${normalizedDieSize})`}
      onRequestClose={onCancel}
      footer={(
        <>
          <button
            type="button"
            className="battle-room-dialog__button battle-room-dialog__button--quiet"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="battle-room-dialog__button battle-room-dialog__button--primary"
            disabled={!valuesAreValid}
            onClick={() => onConfirm && onConfirm(values.map(Number))}
          >
            Confirm
          </button>
        </>
      )}
    >
      <p className="battle-room-dialog__hint">
        Enter each raw die result from 1 to {normalizedDieSize}.
      </p>
      <div className="battle-room-dialog__roll-grid">
        {Array.from({ length: count }).map((_, idx) => (
          <div className="battle-room-dialog__roll-row" key={idx}>
            <label className="battle-room-dialog__label" htmlFor={`battle-room-roll-${idx}`}>
              Roll {idx + 1}
            </label>
            <input
              id={`battle-room-roll-${idx}`}
              aria-label={`Roll ${idx + 1}`}
              type="number"
              value={values[idx] ?? ''}
              onChange={(e) => {
                const next = [...values];
                next[idx] = e.target.value;
                setValues(next);
              }}
              min={1}
              max={normalizedDieSize}
              className="battle-room-dialog__input"
              placeholder={`1-${normalizedDieSize}`}
              data-battle-dialog-autofocus={idx === 0 ? true : undefined}
            />
          </div>
        ))}
      </div>
      {!valuesAreValid && values.some((value) => String(value).trim() !== '') && (
        <p className="battle-room-dialog__hint battle-room-dialog__validation" aria-live="polite">
          Each result must be a whole number from 1 to {normalizedDieSize}.
        </p>
      )}
    </BattleRoomDialog>
  );
};

export default ManualRollModal;
